import React, { FC } from 'react';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';
import { palette } from '../../theme';

interface NewTeamPickerProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onCreate: () => void;
  onJoin: () => void;
  /** Legacy Unlimited coaches who cannot start a catalog plan. */
  createLabel?: string;
  createDescription?: string;
}

interface OptionRowProps {
  icon: React.ReactNode;
  iconColor: string;
  title: string;
  description: string;
  onClick: () => void;
  divider?: boolean;
}

const OptionRow: FC<OptionRowProps> = ({ icon, iconColor, title, description, onClick, divider }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      padding: '14px 16px',
      cursor: 'pointer',
      borderBottom: divider ? `1px solid ${palette.divider}` : 'none',
      transition: 'background-color 0.12s',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        borderRadius: 2,
        backgroundColor: `${iconColor}18`,
        color: iconColor,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="subtitle2" fontWeight={700} lineHeight={1.3}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" lineHeight={1.4}>
        {description}
      </Typography>
    </Box>
  </Box>
);

/** Same popup as New Workout on the schedule: icon, title, and a short description. */
const NewTeamPicker: FC<NewTeamPickerProps> = ({
  anchorEl,
  onClose,
  onCreate,
  onJoin,
  createLabel = 'Create new team',
  createDescription = 'Start a team and choose a plan',
}) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    PaperProps={{
      sx: {
        borderRadius: 2,
        minWidth: 280,
        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
        marginTop: '6px',
        overflow: 'hidden',
      },
    }}
  >
    <OptionRow
      icon={<AddIcon fontSize="small" />}
      iconColor="#1976d2"
      title={createLabel}
      description={createDescription}
      onClick={onCreate}
      divider
    />
    <OptionRow
      icon={<GroupsIcon fontSize="small" />}
      iconColor="#2e7d32"
      title="Join existing team"
      description="Enter a code to join a team"
      onClick={onJoin}
    />
  </Popover>
);

export default NewTeamPicker;
